import { useState } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { User, Phone, MapPin, Trophy, X } from "lucide-react";

const COUNTRY_CODES = [
  { code: "+52", country: "MX", flag: "MX" },
  { code: "+1", country: "US", flag: "US" },
  { code: "+1", country: "CA", flag: "CA" },
];

export function ProfileSetupModal({ user, onComplete, onSkip }) {
  const [name, setName] = useState(user?.name || "");
  const [phoneCountry, setPhoneCountry] = useState("MX");
  const [phone, setPhone] = useState("");
  const [club, setClub] = useState("");
  const [handicapIndex, setHandicapIndex] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (name.trim()) payload.name = name.trim();
      if (phone.trim()) {
        payload.phone = phone.trim();
        const cc = COUNTRY_CODES.find(c => c.country === phoneCountry);
        payload.phone_country_code = cc?.code || "+52";
      }
      if (club.trim()) payload.club = club.trim();
      if (handicapIndex !== "") payload.handicap_index = parseFloat(handicapIndex);

      const resp = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        const updated = await resp.json();
        toast.success("Perfil guardado");
        onComplete(updated);
      } else {
        throw new Error("Error saving profile");
      }
    } catch {
      toast.error("Error al guardar el perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
    } catch {
      // silent
    }
    onSkip();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="profile-setup-modal">
        <div className="bg-[#1B4D3E] px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">Completa tu Perfil</h2>
              <p className="text-sm text-white/70 mt-0.5">Cuéntanos un poco sobre ti</p>
            </div>
            <button onClick={handleSkip} className="text-white/60 hover:text-white transition-colors" data-testid="profile-skip-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
              <User className="w-4 h-4 text-[#1B4D3E]" /> Nombre
            </Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Tu nombre"
              data-testid="profile-name-input"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#1B4D3E]" /> Teléfono
            </Label>
            <div className="flex gap-2">
              <Select value={phoneCountry} onValueChange={setPhoneCountry}>
                <SelectTrigger className="w-[110px]" data-testid="profile-phone-code">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map(c => (
                    <SelectItem key={c.country} value={c.country}>
                      {c.flag} {c.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="10 dígitos"
                maxLength={10}
                className="flex-1"
                data-testid="profile-phone-input"
              />
            </div>
          </div>

          {/* Club */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#1B4D3E]" /> Club
            </Label>
            <Input
              value={club}
              onChange={e => setClub(e.target.value)}
              placeholder="Ej: Guadalajara Country Club"
              data-testid="profile-club-input"
            />
          </div>

          {/* INDEX */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#1B4D3E]" /> INDEX
            </Label>
            <Input
              type="number"
              step="0.1"
              value={handicapIndex}
              onChange={e => setHandicapIndex(e.target.value)}
              placeholder="Ej: 12.5"
              data-testid="profile-index-input"
            />
            <p className="text-xs text-[#64748B]">Tu índice de handicap actual (informativo)</p>
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleSkip}
            data-testid="profile-skip-btn-bottom"
          >
            Omitir
          </Button>
          <Button
            className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white"
            onClick={handleSave}
            disabled={saving}
            data-testid="profile-save-btn"
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
